import React, { useState } from "react"

import * as Sentry from "@sentry/react"
import { Loader2 } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import { useAnonymousVisitor } from "@/hooks/useAnonymousVisitor"
import { useStartApplication } from "@/hooks/useApplicationQueries"
import { jobHasSegment } from "@/utils/stageProgressionUtils"
import {
  buildApplicationErrorUrl,
  getDefaultMessage,
  isRedirectStatus,
  toApplicationStatus,
} from "@/utils/stageRedirectUtils"

import { daysRangeOptions, weekDaysOptions } from "./form-data/availabilityOptions"
import {
  AvailabilitySection,
  CVUploadSection,
  EligibilitySection,
  PersonalInformationSection,
  ReferenceDriverSection,
  VehicleInformationSection,
} from "./form-sections"
import { useBasicDetailsValidation } from "./form-validation/useBasicDetailsValidation"

const BasicDetailsForm: React.FC = () => {
  const { markFormCompleted } = useAnonymousVisitor()
  const { application, generateApplicationId, setApplication, isLoading } = useApplication()
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const [dateInputValue, setDateInputValue] = useState(
    application?.basicDetails.dateOfBirth || "1990-01-01",
  )
  const navigate = useNavigate()
  const { toast } = useToast()
  const { job, isLoading: isLoadingJob } = useActiveJob()
  // Use the React Query mutation hook
  const startApplicationMutation = useStartApplication()

  const { validateForm } = useBasicDetailsValidation(job)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmissionError(null)

    // Normalize email defensively before validation and submission
    const normalizedEmail = (application?.basicDetails?.email || "").trim().toLowerCase()
    const normalizedApplication = {
      ...application,
      basicDetails: {
        ...application.basicDetails,
        email: normalizedEmail,
      },
    }

    // Validate form
    const { isValid } = validateForm(normalizedApplication)
    if (!isValid) return

    // Generate application ID if not already set
    const applicationId = application?.applicationId || (await generateApplicationId())

    try {
      // Get the current origin for redirect URL - use auth-callback
      const redirectUrl = `${window.location.origin}/auth-callback`
      console.log({
        ...normalizedApplication,
        applicationId,
      })
      // Process applicant submission with React Query mutation
      const result = await startApplicationMutation.mutateAsync({
        application: {
          ...normalizedApplication,
          applicationId,
        },
        redirectUrl,
      })

      if (result.success) {
        // Handle progression - it can be a string or an object with nextStage
        const progression = result.progression
        const nextStage = progression?.nextStage

        // Check if we should redirect to application-error page for pending, hold, or rejected
        if (isRedirectStatus(nextStage)) {
          // Store application ID and redirect to application-error page
          if (result.applicationId) {
            localStorage.setItem("currentApplicationId", result.applicationId)
          }
          navigate(
            buildApplicationErrorUrl({
              nextStage: nextStage!,
              applicationId: result.applicationId,
              applicationDate: result.applicationDate,
              message: result.message || getDefaultMessage(toApplicationStatus(nextStage)),
              email: normalizedEmail,
            }),
          )
          return
        }

        markFormCompleted()
        // Navigate to email confirmation page
        navigate(
          `/email-confirmation?applicationId=${applicationId}&email=${encodeURIComponent(
            normalizedEmail,
          )}`,
        )
      } else {
        Sentry.captureException(result, {
          extra: {
            context: "BasicDetailsForm.handleSubmit",
            applicationId: application?.applicationId,
          },
        })
        console.error("Error starting application:", result)

        // Check if the error is due to an existing application (using isDuplicate flag)
        if (result.isDuplicate) {
          // Redirect to the ApplicationError page with the relevant information
          const status = result.status
          navigate(
            buildApplicationErrorUrl({
              nextStage: status,
              applicationId: result.applicationId,
              applicationDate: result.applicationDate,
              message:
                result.message ||
                getDefaultMessage(toApplicationStatus(status)) ||
                "You already have a recent application for this job.",
              email: normalizedEmail,
            }),
          )
        } else if (result.errorCode === "NO_AIRTABLE_RECORD") {
          // Handle NO_AIRTABLE_RECORD error with a user-friendly message
          toast({
            title: "Application Error",
            description:
              "We couldn't find your application record. Please contact support for assistance.",
            variant: "destructive",
          })
          setSubmissionError("Application record not found. Please contact support for assistance.")
        } else {
          // Show a generic error toast for other types of errors
          toast({
            title: "Error",
            description: result.message || "Failed to start application",
            variant: "destructive",
          })
          setSubmissionError(result.message || "Failed to start application")
        }
      }
    } catch (error) {
      console.error("Error submitting application:", error)
      Sentry.captureException(error, {
        extra: {
          context: "BasicDetailsForm.handleSubmit",
          applicationId: application?.applicationId,
        },
      })
      // Show a toast for general errors
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      })
      setSubmissionError("Something went wrong. Please try again.")
    }
  }

  if (isLoading || isLoadingJob) {
    return <Loader2 className="animate-spin" />
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="rounded-lg bg-white shadow">
        <PersonalInformationSection
          application={application}
          setApplication={setApplication}
          dateInputValue={dateInputValue}
          setDateInputValue={setDateInputValue}
        />
      </Card>

      {jobHasSegment(job, "cv-upload") && (
        <Card className="mt-6 rounded-lg bg-white shadow">
          <CVUploadSection application={application} setApplication={setApplication} />
        </Card>
      )}

      {jobHasSegment(job, "reference-driver-id") && (
        <Card className="mt-6 rounded-lg bg-white shadow">
          <ReferenceDriverSection application={application} setApplication={setApplication} />
        </Card>
      )}

      {(jobHasSegment(job, "rtw-eligibility") ||
        jobHasSegment(job, "driving-licence-eligibility")) && (
        <Card className="mt-6 rounded-lg bg-white shadow">
          <EligibilitySection application={application} setApplication={setApplication} />
        </Card>
      )}

      {jobHasSegment(job, "vehicle-information") && (
        <Card className="mt-6 rounded-lg bg-white shadow">
          <VehicleInformationSection application={application} setApplication={setApplication} />
        </Card>
      )}

      {jobHasSegment(job, "availability") && (
        <Card className="mt-6 rounded-lg bg-white shadow">
          <AvailabilitySection
            application={application}
            setApplication={setApplication}
            daysRangeOptions={daysRangeOptions}
            weekDaysOptions={weekDaysOptions}
          />
        </Card>
      )}

      {submissionError && (
        <div
          className="relative rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700"
          role="alert"
        >
          <span className="block sm:inline">{submissionError}</span>
        </div>
      )}

      <div className="px-0 py-0 pb-6 pt-2">
        <Button
          type="submit"
          className="w-full bg-honeycomb text-white hover:bg-amber-500"
          disabled={startApplicationMutation.isPending}
        >
          {startApplicationMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Start Application"
          )}
        </Button>
      </div>
    </form>
  )
}

export default BasicDetailsForm
